import React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Navigate } from 'react-router-dom';
import { PRODUCTS } from '@homeplay/api';
import {
  SiteContext,
  selectIsJackpotsEnabled,
  selectIsJackpotsInitialized,
  selectProducts,
  selectTopGames,
  selectCrashGames,
  selectClub888Games,
  selectGreenDreamGames,
  selectPlaynGoGames,
  selectVisoGames,
  selectNetEntGames,
  selectOnlyPlayGames,
  selectPtcGames,
  selectOlnGames,
  selectUser
} from '@homeplay/app';
import { formatBalance } from '@homeplay/utils';
import { Page, GamesList } from '@homeplay/components';
import UnitSocketContext from '@homeplay/app/src/context/unitSocketContext';
import Button from '@homeplay/components/src/UI/Button';
import jpWinnersIcon from '../../../../shared/assets/images/jackpot/jp-winners.svg';
import { club888GamesOnTouchUnit } from '../Club888';
import classes from './Jackpot.module.scss';

const JACKPOT_MODES = ['optional', 'forced'];

// Same products are not routed for touchscreen units in App
const TOUCH_UNIT_DISABLED_PRODUCTS = [
  PRODUCTS.crashGames.id,
  PRODUCTS.oln.id,
  PRODUCTS.lns.id,
];

const JackpotRules = () => {
  const isJackpotsEnabled = useSelector(selectIsJackpotsEnabled);
  const isJackpotsInitialized = useSelector(selectIsJackpotsInitialized);
  const [winners, setWinners] = useState([]);
  const user = useSelector(selectUser);
  const { siteName, isTouchUnit } = useContext(SiteContext);
  const { jackpotsChannel } = useContext(UnitSocketContext);

  const products = useSelector(selectProducts);
  const topGames = useSelector(selectTopGames);
  const club888Games = useSelector(selectClub888Games);
  const greenDreamGames = useSelector(selectGreenDreamGames);
  const playnGoGames = useSelector(selectPlaynGoGames);
  const visoGames = useSelector(selectVisoGames);
  const netEntGames = useSelector(selectNetEntGames);
  const onlyPlayGames = useSelector(selectOnlyPlayGames);
  const ptcGames = useSelector(selectPtcGames);
  const olnGames = useSelector(selectOlnGames);
  const crashGames = useSelector(selectCrashGames);

  const gamesData = useMemo(() => {
    // Product lists go first so that duplicates from topGames are dropped
    const allGames = [
      ...(ptcGames || []),
      ...(club888Games || []),
      ...(greenDreamGames || []),
      ...(playnGoGames || []),
      ...(visoGames || []),
      ...(netEntGames || []),
      ...(onlyPlayGames || []),
      ...(crashGames || []),
      ...(olnGames || []),
      ...(topGames || []),
    ];

    const enabledProductIds = products
      ? new Set(products.map((product) => product.id))
      : null;
    const isClub888Restricted =
      isTouchUnit && process.env.REACT_APP_ENV === 'prod';
    const uniqSlugs = new Set();

    return allGames.filter((game) => {
      if (!JACKPOT_MODES.includes(game.jackpotMode)) return false;
      if (uniqSlugs.has(game.slug)) return false;
      if (enabledProductIds && !enabledProductIds.has(game.productId)) {
        return false;
      }
      if (isTouchUnit && TOUCH_UNIT_DISABLED_PRODUCTS.includes(game.productId)) {
        return false;
      }
      if (
        isClub888Restricted &&
        game.productId === PRODUCTS.club888.id &&
        !club888GamesOnTouchUnit.includes(game.slug)
      ) {
        return false;
      }

      uniqSlugs.add(game.slug);
      return true;
    });
  }, [
    products,
    isTouchUnit,
    topGames,
    club888Games,
    greenDreamGames,
    playnGoGames,
    visoGames,
    netEntGames,
    onlyPlayGames,
    ptcGames,
    crashGames,
    olnGames,
  ]);

  useEffect(() => {
    let isActive = true;
    const fetchWinners = async () => {
      if (!jackpotsChannel) return;
      try {
        const res = await jackpotsChannel.push('get_recently_won_jackpots');
        if (isActive && res?.recently_won) {
          const sorted = [...res.recently_won].sort(
            (a, b) => new Date(b.timestamp).valueOf() - new Date(a.timestamp).valueOf()
          );
          setWinners(sorted);
        }
      } catch (_) {}
    };
    fetchWinners();
    return () => {
      isActive = false;
    };
  }, [jackpotsChannel]);

  if (!isJackpotsInitialized) {
    return null;
  }

  if (!isJackpotsEnabled) {
    return <Navigate to={`/${siteName ? siteName + '/' : ''}`} replace />;
  }

  return (
    <Page headerContent={'Jackpot'} customClass={classes.JackpotRulesPage}>
      <div className="container">
        <div className={classes.RulesButtonWrapper}>
          <a href="#rulesSection">
            <Button>RULES</Button>
          </a>
        </div>
        
        <section className={classes.GamesSection}>
          {gamesData && gamesData.length > 0 ? <GamesList games={gamesData} /> : null}
        </section>

        <section className={classes.RulesSection}>
          <h2 id="rulesSection" className={classes.RulesTitle}>RULES</h2>
          <div className={classes.RulesGrid}>
            <div className={classes.RuleItem}>
              <div className={classes.Bullet}>1</div>
              <div>
                <h4>How to Qualify</h4>
                <p>
                  {!user ? (
                    <>
                      <Link to={`/${siteName ? siteName + '/' : ''}auth`}>Log in</Link> or <Link to={`/${siteName ? siteName + '/' : ''}auth`}>register</Link> on the website.
                    </>
                  ) : (
                    <>Log in or register on the website.</>
                  )}{' '}
                  Play any game marked with the “Jackpot” badge
                  and place a qualifying stake <span className={classes.Amount}>{formatBalance(100)}</span> or more.
                </p>
              </div>
            </div>
            <div className={classes.RuleItem}>
              <div className={classes.Bullet}>2</div>
              <div>
                <h4>How It Works</h4>
                <p>
                  The Jackpot can be triggered randomly on any spin with the qualified stake, except bonus spins.
                </p>
              </div>
            </div>
            <div className={classes.RuleItem}>
              <div className={classes.Bullet}>3</div>
              <div>
                <h4>Types of Jackpots</h4>
                <p>
                  There are different Jackpot types, such as Grand, Major, and Minor. Each game can have different Jackpots
                  and you can win any of them.
                </p>
              </div>
            </div>
            <div className={classes.RuleItem}>
              <div className={classes.Bullet}>4</div>
              <div>
                <h4>Winning</h4>
                <p>
                  If your spin qualifies and all conditions are met, you could win the Jackpot. A winning banner will appear,
                  and the prize will be added to your balance.
                </p>
              </div>
            </div>
            <div className={classes.RuleItem}>
              <div className={classes.Bullet}>5</div>
              <div>
                <h4>Latest Results</h4>
                <p>
                  Check out the latest Jackpot winners on our site. For privacy, only the first and last letters of the winner's name will be shown.
                </p>
              </div>
            </div>
          </div>
        </section>

        {winners.length > 0 && (
          <section className={classes.WinnersSection}>
            <div className={classes.WinnersCard}>
              <div className={classes.WinnersHeader}>
                <img className={classes.WinnersIcon} src={jpWinnersIcon} alt="Latest winners" />
                <span>Latest winners</span>
              </div>
              <div className={classes.WinnersTableWrap}>
                <table className={classes.WinnersTable}>
                  <thead>
                    <tr>
                      <th className={classes.Left}>User name</th>
                      <th className={classes.Right}>Type</th>
                      <th className={classes.Right}>Date</th>
                      <th className={classes.Right}>Win</th>
                    </tr>
                  </thead>
                  <tbody>
                    {winners.map((w, idx) => (
                      <tr key={idx}>
                        <td className={classes.Left}>{w.first_name}</td>
                        <td className={classes.Right}>{w.name}</td>
                        <td className={classes.Right}>{new Date(w.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}</td>
                        <td className={classes.RightColored}>{formatBalance(w.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}
      </div>
    </Page>
  );
};

export default JackpotRules;
